import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  displayNameFromToken,
  truncateName,
} from '../../shared/currentUser';
import { useDismissableMenu } from './useDismissableMenu';

/**
 * The signed-in user's name in the header, opening a small menu.
 *
 * Hand-rolled rather than reactstrap's Dropdown: Bootstrap's CSS is
 * loaded globally, so that component arrives looking like Bootstrap,
 * and this has to look like the rest of the header.
 */

interface IUserMenuProps {
  token: string | null;
  onLogout: () => void;
}

export const UserMenu = ({ token, onLogout }: IUserMenuProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const fullName = displayNameFromToken(token);
  const shownName = truncateName(fullName);

  useDismissableMenu(open, () => setOpen(false), containerRef);

  return (
    <div className="header-menu" ref={containerRef}>
      <div
        className="media-a navlink header-menu__trigger"
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={open}
        // The full name, since the visible one may be cut short.
        title={fullName}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((current) => !current);
          }
        }}
        onClick={() => setOpen((current) => !current)}
      >
        {shownName}
        <i
          className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'} header-menu__caret`}
        ></i>
      </div>
      {open && (
        <div className="header-menu__panel" role="menu">
          <Link
            to="/profile"
            className="header-menu__item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            <i className="bi bi-person"></i> Profile
          </Link>
          <div
            className="header-menu__item"
            role="menuitem"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setOpen(false);
                onLogout();
              }
            }}
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            <i className="bi bi-box-arrow-right"></i> Logout
          </div>
        </div>
      )}
    </div>
  );
};
